"use client";

import { Fragment } from "react";
import { useSelector } from "react-redux";
import { Divider, DocumentIcon } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { IconButtonWrapper } from "@/components/atoms/button-wrapper/button-wrapper";
import TextSelectionButton from "@/components/atoms/text-selection-button/text-selection-button";
import AMTPaymentSelector from "@/components/molecules/quote-section/amt-payment-selector/amt-payment-selector";
import withBrand from "@/components/with-brand";
import { GADGET_COVER } from "@/constants";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import { RootState } from "@/store/root-reducer";
import { shouldExcludeDocument } from "@/utils/document.utils";
import { PriceAndDocAreaContentType } from "@/utils/schema/quote-page-schema.utils";

import { brandStyles, openInNewTab } from "./docs.utils";
import DocumentItem from "./document-item";
import Styles from "./price-and-docs.module.css";

type NestedObject = {
  [key: string]: {
    linkUrl: string;
    linkText: string;
  };
};

export type RelatedDoc = {
  linkUrl: string;
  linkText: string;
  linkId: string;
};

function PriceAndDocs({
  priceAndDocAreaContent,
  brand = "staysure",
}: Readonly<{
  priceAndDocAreaContent: PriceAndDocAreaContentType | null;
  brand?: "staysure" | "avanti";
}>) {
  const [isdesktop, isReady] = useMediaQuery("(min-width: 904px)");
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { paymentDetails, tripDetails, gadgetMigration } = attributes;

  const { current } = useWizard();
  // Only use new gadget cover when isBeforeGadgetMigration is explicitly false
  const isNewGadgetCover = gadgetMigration?.isBeforeGadgetMigration === false;
  let isEligible = false;
  let pclData = null;
  let fullPayment = "0.00";

  if (paymentDetails) {
    const { paymentOptions } = paymentDetails;
    const [
      {
        cardPayment: {
          premiumCreditPayment: [{ pcl }],
          card: { price },
        },
      },
    ] = paymentOptions;

    if (tripDetails?.policyType === "SINGLE_TRIP") {
      isEligible = pcl.eligible;
    } else if (current.brandSpecificParams?.isEnablePaymentOption) {
      isEligible = !pcl.eligible;
    } else {
      isEligible = pcl.eligible;
    }

    pclData = pcl;
    fullPayment = price;
  }

  const mapDocuments = (data: any, dataNodeArray: string[]) => {
    const findOption = (code: string) =>
      attributes.priceResult?.optionalExtras.find(
        (option) => option.code === code
      );

    const { selected: gadgetCoverSelected, state: gadgetCoverIncluded } =
      findOption("GADGET_COVER")?.price[0] ?? {};

    const { selected: carExcessSelected } =
      findOption("CAR_HIRE_EXCESS_WAIVER")?.price[0] ?? {};

    const dataArr = dataNodeArray.map((item: keyof NestedObject) => {
      const dataNode = data[`${item}`];

      const spainDocs =
        dataNode.linkId === "spain-doc" ||
        dataNode.linkId === "spain-key-facts";
      const portugalDocs =
        dataNode.linkId === "portugal-doc" ||
        dataNode.linkId === "portugal-key-facts";
      const franceDocs =
        dataNode.linkId === "france-doc" ||
        dataNode.linkId === "france-key-facts";

      const policy =
        attributes.tripDetails?.policyPackage?.toLocaleLowerCase() ?? "";
      if (
        !(
          (Array.isArray(dataNode.relevantCoverTypes) &&
            dataNode?.relevantCoverTypes
              .filter((type: string) => {
                return !(
                  dataNode.relevantCoverTypes?.length === 1 &&
                  (type === "classic" || type === "deluxe")
                );
              })
              .includes(policy)) ||
          dataNode.relevantCoverTypes.includes(
            tripDetails?.goingOnCruise ? `${policy}_cruise` : policy
          )
        )
      ) {
        return null;
      }

      // Hide the old gadget cover if new gadget cover is enabled
      if (isNewGadgetCover && dataNode?.linkId === GADGET_COVER) {
        return null;
      }

      if (
        dataNode.linkId === "gadget-cover" &&
        ((gadgetCoverIncluded !== "INCLUDED" &&
          gadgetCoverIncluded === "DISABLED") ||
          (gadgetCoverIncluded === "ENABLED" && !gadgetCoverSelected))
      ) {
        return null;
      }
      if (dataNode.linkId === "car-excess" && !carExcessSelected) {
        return null;
      }
      if (
        shouldExcludeDocument(
          tripDetails?.fromLocation,
          franceDocs,
          portugalDocs,
          spainDocs,
          current
        )
      ) {
        return null;
      }
      return {
        link: dataNode.linkUrl,
        label: dataNode.linkText,
        id: dataNode.linkId,
      };
    });
    return dataArr;
  };

  if (!priceAndDocAreaContent) return null;

  const {
    documentsTitle = "",
    fullPolicyDocumentTitle = "",
    priceResultsTitle = "",
    monthlyPaySummaryTitle = "",
    priceSummaryDescription = "",
    relatedDocs,
    policyPriceRow = "",
    totalCreditChargeRow = "",
    intrestRow = "",
    fixedInterestRateLabel = "",
    initialPaymentRow = "",
    installmentsRow = "",
    totalPriceRow = "",
  } = priceAndDocAreaContent;

  const summaryTableData = [
    {
      key: "totalCost",
      title: policyPriceRow,
    },
    {
      key: "costOfCredit",
      title: totalCreditChargeRow,
    },
    {
      key: "annualPercentageRate",
      title: intrestRow,
    },
    {
      key: "fixedInterestRate",
      title: fixedInterestRateLabel,
    },
    {
      key: "collectionAmount",
      title: installmentsRow,
    },
    {
      key: "depositAmount",
      title: initialPaymentRow,
    },
  ];

  const renderDocumentLinks = ({
    isEligibleCheck,
  }: {
    isEligibleCheck: boolean;
  }) => {
    const dataNodeArray = Object.keys(relatedDocs);
    const documentData = mapDocuments(relatedDocs, dataNodeArray);
    let displayDivider = 0;
    return documentData
      .map(
        (
          doc: { link: string; label: string; id: string } | null,
          index: number
        ) => {
          if (doc) {
            displayDivider = displayDivider === 0 ? 1 : 2;
            return (
              <Fragment key={doc.label}>
                {displayDivider === 2 && (
                  <Divider
                    key={`${doc.label}-divider`}
                    className={`${
                      isEligible &&
                      paymentDetails !== null &&
                      paymentDetails !== undefined
                        ? ""
                        : "lg:hidden "
                    }  ${displayDivider !== 2 ? "hidden" : ""}`}
                    margin={isdesktop ? 12 : 8}
                    color={brandStyles[brand].dividerColor}
                  />
                )}
                <DocumentItem
                  name={doc.label}
                  link={doc.link}
                  isEligible={isEligibleCheck}
                  id={index}
                  brand={brand}
                />
              </Fragment>
            );
          }
          return null;
        }
      )
      .filter(Boolean);
  };

  if (!isReady) return null;

  return (
    <div
      data-testid="important-document-test-id"
      className={`grid grid-cols-12 lg:gap-spacing-l md:gap-spacing-xl
       max-w-[1200px] lg:mx-auto md:mx-spacing-xxl mx-spacing-l w-auto`}
    >
      {isEligible && paymentDetails && (
        <div className="lg:col-span-7 lg:max-w-[672px] col-span-12 md:mt-0 md:flex w-auto justify-center mb-spacing-l md:mb-0 h-fit">
          <AMTPaymentSelector
            pclData={pclData}
            priceResultsTitle={priceResultsTitle}
            monthlyPaySummaryTitle={monthlyPaySummaryTitle}
            priceSummaryDescription={priceSummaryDescription}
            totalPriceRow={totalPriceRow}
            summarayData={summaryTableData}
            fullPayAmount={fullPayment}
          />
        </div>
      )}

      <div
        className={`${
          isEligible && paymentDetails
            ? "lg:col-span-5 col-span-12 lg:max-w-[480px] lg:flex lg:justify-end"
            : "col-span-12"
        } h-fit my-0 w-auto relative bg-surface-neutral-option-1 
  md:py-spacing-xxl py-spacing-m md:px-spacing-xxxl px-[19px] rounded-radius-xxl`}
      >
        <div>
          <div
            className={` mb-spacing-m lg:mb-spacing-l font-header-font ${
              isEligible && paymentDetails
                ? "lg:leading-[110%] leading-[115%] text-[26px] md:text-[39px] lg:text-[31px]"
                : "leading-[115%] text-[26px] md:text-[39px] "
            }  font-[400] text-text-title`}
          >
            {documentsTitle}
          </div>
          <div
            className={`${
              isEligible &&
              paymentDetails !== null &&
              paymentDetails !== undefined
                ? ""
                : `lg:flex lg:flex-wrap lg:justify-self-auto lg:overflow-hidden ${brandStyles[brand].gap} lg:gap-y-spacing-l`
            } ${Styles.docSvg}`}
          >
            <div
              className={` ${
                isEligible &&
                paymentDetails !== null &&
                paymentDetails !== undefined
                  ? ""
                  : "lg:flex lg:flex-col"
              } items-start border-gray-300 ${!isdesktop && "w-full"}`}
            >
              <div className="flex items-center h-fit" key="full-doc">
                <IconButtonWrapper
                  id="document-icon-default"
                  hierarchy="tertiary"
                  btnType="icon"
                  isDisabled
                  icon={<DocumentIcon stroke="rgba(39, 36, 39, 1)" />}
                  className="!max-w-[24px] h-[24px] text-[24px] text-text-on-primary !max-h-[27px] !my-0 !px-0 !py-0 !bg-surface-neutral-option-1 active:!bg-surface-neutral-option-1 hover:!bg-surface-neutral-option-1"
                />
                <span
                  className={`text-sm md:text-xs font-semibold leading-[27px]  ${
                    isEligible
                      ? "lg:pl-spacing-xxs pl-spacing-xs"
                      : "pl-spacing-xs"
                  }`}
                >
                  <TextSelectionButton
                    id="document-default"
                    label={fullPolicyDocumentTitle}
                    className={` text-[18px] font-body-font font-[600] align-top leading-[27px] !text-left h-fit
                   hover:text-interactive-secondary-default hover:bg-surface-neutral-option-1 !px-0`}
                    onClick={() =>
                      openInNewTab(
                        attributes.dynamicDocuments?.documentUrls.POLICY_WORDING
                      )
                    }
                  />
                </span>
              </div>
            </div>
            <Divider
              className={`${
                isEligible &&
                paymentDetails !== null &&
                paymentDetails !== undefined
                  ? ""
                  : "lg:hidden "
              } `}
              margin={isdesktop ? 12 : 8}
              color={brandStyles[brand].dividerColor}
            />
            {renderDocumentLinks({
              isEligibleCheck:
                isEligible &&
                paymentDetails !== null &&
                paymentDetails !== undefined,
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default withBrand(PriceAndDocs);
